import { useCallback, useEffect, useMemo, useState } from "react";
import { Search, Pencil, Trash2, Check, X, Plus } from "lucide-react";
import { epicengageSupabasev1 } from "../../../../../integrations/supabase/client";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../../../ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../../../ui/alert-dialog";
import { Input } from "../../../../ui/input";
import { Button } from "../../../../ui/button";
import { useToast } from "../../../../../hooks/use-toast";
import { cn } from "../../../../../lib/utils";
import { TAG_PALETTE as PALETTE, TAG_PALETTE_KEYS as PALETTE_KEYS, fallbackTagColorKey as fallbackColorKey, swatchForTag as swatchFor } from "../../../../../lib/tagColors";

type Props = {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  merchantId: string;
  onChanged?: () => void;
};

type TagRow = { tag: string; count: number; color: string };

export const ManageTagsDialog = ({ open, onOpenChange, merchantId, onChanged }: Props) => {
  const { toast } = useToast();
  const [rows, setRows] = useState<TagRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState<string | null>(null);
  const [editValue, setEditValue] = useState("");
  const [pendingDelete, setPendingDelete] = useState<TagRow | null>(null);
  const [busy, setBusy] = useState(false);

  // Create row state
  const [newName, setNewName] = useState("");
  const [newColor, setNewColor] = useState<string>("blue");
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    if (!merchantId) return;
    setLoading(true);
    const [{ data: master, error: e1 }, { data: assigned, error: e2 }] = await Promise.all([
      epicengageSupabasev1.from("merchant_tags").select("tag, color").eq("merchant_id", merchantId),
      (epicengageSupabasev1 as any).from("active_customer_tags").select("tag, customer_id").eq("merchant_id", merchantId),
    ]);
    if (e1 || e2) {
      toast({ title: "Could not load tags", description: (e1 ?? e2)?.message, variant: "destructive" });
      setRows([]); setLoading(false); return;
    }
    const counts = new Map<string, Set<string>>();
    for (const r of (assigned as { tag: string; customer_id: string }[] | null) ?? []) {
      if (!counts.has(r.tag)) counts.set(r.tag, new Set());
      counts.get(r.tag)!.add(r.customer_id);
    }
    const colorByTag = new Map<string, string>();
    for (const m of master ?? []) colorByTag.set(m.tag, m.color);

    // Union of master tags and any tags only present in customer_tags (legacy)
    const allTags = new Set<string>([...colorByTag.keys(), ...counts.keys()]);
    const list: TagRow[] = Array.from(allTags)
      .map((tag) => ({
        tag,
        count: counts.get(tag)?.size ?? 0,
        color: colorByTag.get(tag) ?? fallbackColorKey(tag),
      }))
      .sort((a, b) => a.tag.localeCompare(b.tag));
    setRows(list);
    setLoading(false);
  }, [merchantId, toast]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter((r) => r.tag.toLowerCase().includes(s));
  }, [rows, search]);

  const createTag = async () => {
    const name = newName.trim().toLowerCase();
    if (!name) return;
    if (rows.some((r) => r.tag === name)) {
      toast({ title: "Tag already exists", description: `"${name}" is already in your list.`, variant: "destructive" });
      return;
    }
    setCreating(true);
    const { error } = await epicengageSupabasev1
      .from("merchant_tags")
      .insert({ merchant_id: merchantId, tag: name, color: newColor });
    setCreating(false);
    if (error) {
      toast({ title: "Could not create tag", description: error.message, variant: "destructive" });
      return;
    }
    toast({ title: "Tag created", description: `"${name}" is ready to use.` });
    setNewName("");
    setNewColor("blue");
    await load();
    onChanged?.();
  };

  const beginEdit = (tag: string) => { setEditing(tag); setEditValue(tag); };
  const cancelEdit = () => { setEditing(null); setEditValue(""); };

  const saveRename = async (oldTag: string) => {
    const next = editValue.trim().toLowerCase();
    if (!next || next === oldTag) { cancelEdit(); return; }
    if (rows.some((r) => r.tag === next)) {
      toast({ title: "Tag already exists", description: `"${next}" is already in use.`, variant: "destructive" });
      return;
    }
    setBusy(true);
    const [r1, r2] = await Promise.all([
      epicengageSupabasev1.from("customer_tags").update({ tag: next }).eq("merchant_id", merchantId).eq("tag", oldTag),
      epicengageSupabasev1.from("merchant_tags").update({ tag: next }).eq("merchant_id", merchantId).eq("tag", oldTag),
    ]);
    setBusy(false);
    const err = r1.error ?? r2.error;
    if (err) {
      toast({ title: "Rename failed", description: err.message, variant: "destructive" });
      return;
    }
    toast({ title: "Tag renamed", description: `"${oldTag}" → "${next}"` });
    cancelEdit();
    await load();
    onChanged?.();
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    setBusy(true);
    const [r1, r2] = await Promise.all([
      epicengageSupabasev1.from("customer_tags").delete().eq("merchant_id", merchantId).eq("tag", pendingDelete.tag),
      epicengageSupabasev1.from("merchant_tags").delete().eq("merchant_id", merchantId).eq("tag", pendingDelete.tag),
    ]);
    setBusy(false);
    const err = r1.error ?? r2.error;
    if (err) {
      toast({ title: "Delete failed", description: err.message, variant: "destructive" });
      return;
    }
    toast({
      title: "Tag deleted",
      description: `Removed "${pendingDelete.tag}"${pendingDelete.count ? ` from ${pendingDelete.count} customer${pendingDelete.count === 1 ? "" : "s"}` : ""}.`,
    });
    setPendingDelete(null);
    await load();
    onChanged?.();
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Manage tags</DialogTitle>
            <DialogDescription>
              Create, rename, or delete tags. Tags created here can be assigned to customers, used during imports, and used as audience filters.
            </DialogDescription>
          </DialogHeader>

          {/* Create new tag */}
          <div className="rounded-md border border-border z-40 p-3 space-y-3 bg-muted/30">
            <div className="flex items-center gap-2">
              <Input
                placeholder="Tag name"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") { e.preventDefault(); createTag(); }
                }}
                className="flex-1 h-9"
                maxLength={60}
              />
              <Button
                onClick={createTag}
                disabled={creating || !newName.trim()}
                size="sm"
                className="h-9"
                aria-label="Create tag"
              >
                <Plus className="h-4 w-4" />
                Add
              </Button>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs text-muted-foreground mr-1">Colour:</span>
              {PALETTE_KEYS.map((key) => {
                const sw = PALETTE[key];
                const selected = newColor === key;
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setNewColor(key)}
                    aria-label={`Select ${key}`}
                    aria-pressed={selected}
                    className={cn(
                      "h-6 w-6 rounded-full border transition-all",
                      selected ? "ring-2 ring-ring ring-offset-2 ring-offset-background border-foreground/40" : "border-black/10 hover:scale-110",
                    )}
                    style={{ background: sw.dot }}
                  />
                );
              })}
            </div>
            {newName.trim() && (
              <div className="text-xs text-muted-foreground">
                Preview:{" "}
                <span
                  className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ml-1"
                  style={{ background: swatchFor(newColor).bg, color: swatchFor(newColor).fg }}
                >
                  {newName.trim().toLowerCase()}
                </span>
              </div>
            )}
          </div>

          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search tags…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>

          <div className="border border-border rounded-md max-h-80 overflow-y-auto">
            {loading ? (
              <p className="p-6 text-sm text-center text-muted-foreground">Loading…</p>
            ) : filtered.length === 0 ? (
              <p className="p-6 text-sm text-center text-muted-foreground">
                {rows.length === 0 ? "No tags yet. Create your first tag above." : "No tags match your search."}
              </p>
            ) : (
              <ul className="divide-y divide-border">
                {filtered.map((r) => {
                  const sw = swatchFor(r.color);
                  const isEditing = editing === r.tag;
                  return (
                    <li key={r.tag} className="flex items-center gap-3 px-3 py-2">
                      <span
                        className="inline-block h-3 w-3 rounded-full shrink-0 border border-black/5"
                        style={{ background: sw.dot }}
                        aria-hidden
                      />
                      {isEditing ? (
                        <Input
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") { e.preventDefault(); saveRename(r.tag); }
                            if (e.key === "Escape") { e.preventDefault(); cancelEdit(); }
                          }}
                          autoFocus
                          className="h-8 flex-1"
                          maxLength={60}
                        />
                      ) : (
                        <span
                          className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
                          style={{ background: sw.bg, color: sw.fg }}
                        >
                          {r.tag}
                        </span>
                      )}
                      <span className="ml-auto text-xs text-muted-foreground whitespace-nowrap">
                        {r.count} customer{r.count === 1 ? "" : "s"}
                      </span>
                      {isEditing ? (
                        <>
                          <Button size="icon" variant="ghost" className="h-8 w-8" disabled={busy} onClick={() => saveRename(r.tag)} aria-label="Save">
                            <Check className="h-4 w-4" />
                          </Button>
                          <Button size="icon" variant="ghost" className="h-8 w-8" onClick={cancelEdit} aria-label="Cancel">
                            <X className="h-4 w-4" />
                          </Button>
                        </>
                      ) : (
                        <>
                          <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => beginEdit(r.tag)} aria-label={`Rename ${r.tag}`}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive hover:text-destructive" onClick={() => setPendingDelete(r)} aria-label={`Delete ${r.tag}`}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <div className="flex justify-end pt-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!pendingDelete} onOpenChange={(v) => !v && setPendingDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete tag "{pendingDelete?.tag}"?</AlertDialogTitle>
            <AlertDialogDescription>
              Deleting this tag removes it from all customers
              {pendingDelete ? ` (${pendingDelete.count} affected)` : ""}. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); confirmDelete(); }}
              disabled={busy}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete tag
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
